import { motion } from "framer-motion";
import Image from "../../../elements/Image";
import ContactImage from "../../../../assets/images/contact.webp";
import Input from "../../../form/Input";
import Button from "../../../form/Button";
import Textarea from "../../../form/Textarea";
import Title from "../../../elements/Title";
import Form from "../../../common/Form";
import { enquiryAddEdit } from "../../../../services/enquiryService";
import { useMutation, useQuery } from "@tanstack/react-query";
import useForm from "../../../../hooks/useForm";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { lngDetect } from "../../../common/lngDetect";
import { postFrontDetails } from "../../../../services/postService";

const validation = {
  name: { required: true, message: "Full name is required" },
  email: [
    { required: true, message: "Email is required" },
    { type: "email", message: "Please enter a valid email address" },
  ],
  subject: { required: true, message: "Subject is required" },
  message: { required: true, message: "Message is required" },
};

const Contact = () => {
  const { data } = useSelector((state) => state?.profile);

  const { data: contactData } = useQuery({
    queryKey: ["front-contact-section"],
    queryFn: async () => {
      const res = await postFrontDetails({
        slug: "contact-section",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.data;
    },
  });

  const newContactData = {
    title: lngDetect(contactData?.content)?.title,
    description: lngDetect(contactData?.content)?.subTitle,
    image: contactData?.mediaGallery?.[0]?.url,
  };

  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    resetField,
    formValidate,
  } = useForm({
    validationRules: validation,
    initialState: {
      name: "",
      email: "",
      subject: "",
      message: "",
    },
  });

  const { mutate: onSubmit, isPending: isLoading } = useMutation({
    mutationFn: async (val) => {
      return await enquiryAddEdit({
        ...val,
        type: "contact",
        organization: data?.orgMemberships?.[0]?.orgId,
      });
    },
    onSuccess: (res) => {
      if (res?.status === 201) {
        resetField();
        toast.success(res?.message || "Form submitted successfully");
      }
    },
    onError: (error) => {
      console.error("Contact form error:", error);
    },
  });

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <section
      className="relative py-10 xls:py-24 z-0 overflow-hidden"
      id="contact"
    >
      {/* Background Gradients */}
      <motion.div
        animate={{ opacity: [0.1, 0.2, 0.1], scale: [1, 1.2, 1] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-0 right-0 w-[700px] h-[700px] bg-electric-violet/30 rounded-full blur-[140px] z-[-1]"
      />

      <motion.div
        animate={{ opacity: [0.1, 0.2, 0.1], scale: [1.1, 1, 1.1] }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2,
        }}
        className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-yellow-400/20 rounded-full blur-[120px] z-[-1]"
      />

      <div className="w-full sm:max-w-xl md:max-w-2xl lg:max-w-4xl xls:max-w-6xl xl:max-w-7xl 2xl:max-w-8xl 3xl:max-w-9xl mx-auto px-4">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* Header */}
          <div className="text-center space-y-6 mb-16 lg:mb-20">
            {/* Subtitle */}
            <motion.div
              className="flex items-center justify-center"
              variants={item}
            >
              <Title title={newContactData?.title} />
            </motion.div>

            {/* Title */}
            <motion.div
              className="mx-auto w-full max-w-2xl xl:max-w-3xl text-center"
              variants={item}
            >
              <h2 className="text-2xl sm:text-3xl xl:text-4xl font-bold leading-normal">
                {newContactData?.description}
              </h2>
            </motion.div>
          </div>

          {/* Contact Content */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Side - AI UI Visual */}
            {/* Left Side - Image */}
            <motion.div variants={item} className="space-y-10 relative">
              {/* Floating Accent Dots */}
              <motion.div
                animate={{ y: [0, -12, 0] }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute -top-8 right-10 w-3 h-3 bg-electric-violet rounded-full blur-sm opacity-70"
              />
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{
                  duration: 7,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1,
                }}
                className="absolute bottom-6 left-10 w-2 h-2 bg-yellow-400 rounded-full blur-sm opacity-60"
              />

              {/* Image Container */}
              <div className="relative max-w-80 mx-auto">
                <motion.div
                  animate={{ y: [0, -12, 0] }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="relative"
                >
                  {/* Soft Glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-electric-violet/30 to-yellow-400/20 rounded-[3rem] blur-3xl" />

                  {/* Shimmer Overlay */}
                  <div className="absolute inset-0 rounded-[3rem] overflow-hidden pointer-events-none">
                    <motion.div
                      animate={{ x: ["-100%", "100%"] }}
                      transition={{
                        duration: 6,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                      className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                    />
                  </div>

                  {/* Image */}
                  <div className="relative rounded-[3rem] overflow-hidden border border-white/10">
                    <Image
                      src={newContactData?.image}
                      alt="Contact Us"
                      effect="blur"
                      classes="w-full"
                    />
                  </div>
                </motion.div>
              </div>
            </motion.div>

            {/* Right Side - Contact Form */}
            <motion.div variants={item}>
              <Form onSubmit={onSubmit} handleSubmit={handleSubmit}>
                <div className="space-y-6">
                  {/* Name Input */}
                  <Input
                    label="Full Name"
                    labelClasses="text-base font-semibold text-white/80"
                    inputType="text"
                    inputPlaceholder="Enter your name"
                    onChange={handleChange}
                    inputName="name"
                    required
                    value={values?.name}
                    inputClasses="!h-14 !bg-white/5 !border-white/10 !text-white placeholder:!text-white/40 focus:!border-electric-violet/50 focus:!bg-white/10 !rounded-2xl"
                    {...formValidate(errors, "name")}
                  />

                  {/* Email Input */}
                  <Input
                    label="Email Address"
                    labelClasses="text-base font-semibold text-white/80"
                    inputType="email"
                    inputPlaceholder="Enter your email"
                    onChange={handleChange}
                    inputName="email"
                    required
                    value={values?.email}
                    inputClasses="!h-14 !bg-white/5 !border-white/10 !text-white placeholder:!text-white/40 focus:!border-electric-violet/50 focus:!bg-white/10 !rounded-2xl"
                    {...formValidate(errors, "email")}
                  />

                  {/* Subject Input */}
                  <Input
                    label="Subject"
                    labelClasses="text-base font-semibold text-white/80"
                    inputType="text"
                    inputPlaceholder="What is this about?"
                    onChange={handleChange}
                    inputName="subject"
                    required
                    value={values?.subject}
                    inputClasses="!h-14 !bg-white/5 !border-white/10 !text-white placeholder:!text-white/40 focus:!border-electric-violet/50 focus:!bg-white/10 !rounded-2xl"
                    {...formValidate(errors, "subject")}
                  />

                  {/* Message Textarea */}
                  <Textarea
                    label="Message"
                    labelClasses="text-base font-semibold text-white/80"
                    inputName="message"
                    inputPlaceholder="Tell us more..."
                    onChange={handleChange}
                    required
                    inputValue={values?.message}
                    inputClasses="!h-32 !bg-white/5 !border-white/10 !text-white placeholder:!text-white/40 focus:!border-electric-violet/50 focus:!bg-white/10 !rounded-2xl"
                    {...formValidate(errors, "message")}
                  />

                  {/* Submit Button */}
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      buttonType="submit"
                      buttonLabel="Send Message"
                      buttonIcon="fa-solid fa-paper-plane"
                      buttonIconPosition="left"
                      buttonClasses="!w-full !h-14 !rounded-full font-bold text-base !bg-gradient-to-r !from-yellow-400 !to-orange-500 !text-[#320051] hover:!shadow-2xl hover:!shadow-yellow-400/30 !transition-all !duration-300"
                      {...(isLoading && {
                        loading: true,
                        isDisable: true,
                      })}
                    />
                  </motion.div>
                </div>
              </Form>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;
